import { type FC, type ForwardedRef, type MouseEvent, type ReactNode } from 'react';

/* eslint-disable-next-line import/order -- Disabled due to the comment above the next import that
   disables the "no-restricted-imports" rule. */
import CloseOutlined from '@mui/icons-material/CloseOutlined';

/* eslint-disable-next-line import/order -- Disabled due to the comment above the next import that
   disables the "no-restricted-imports" rule. */
import InputAdornment from '@mui/material/InputAdornment';

/* eslint-disable-next-line no-restricted-imports */
import RootTextField, { type TextFieldProps as RootTextFieldProps } from '@mui/material/TextField';

import type * as types from './types';

import { type SxPropsCallback } from '../../types/mui/sx';
import { mergeSx } from '../../util/mui/sx';
import { DefaultInputSize, type InputSize } from '../../util/theme/elements/inputs';
import { Icon } from '../icons/Icon';
import { type IconProp } from '../icons/types';

import { HelperText } from './HelperText';
import { TextInputClearButton } from './TextInputClearButton';
import { type TextFieldVariant } from './types';

const TextFieldWarningLevelSx: SxPropsCallback = t => ({
  '& > .MuiInputBase-root.MuiOutlinedInput-root.Mui-error': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: t.palette.other.yellowWarning,
    },
  },
  '& > .MuiInputLabel-asterisk.Mui-error': { color: t.palette.other.yellowWarning },
  '& > .MuiInputLabel-shrink.Mui-error': { color: t.palette.other.yellowWarning },
  '& > .MuiTypography-fieldHelperText.Mui-error': {
    ...t.typography.fieldWarningText,
  },
});

export type TextFieldClearEvent = MouseEvent<HTMLButtonElement>;

export type TextFieldInputProps<Variant extends types.TextFieldVariant = types.TextFieldVariant> = {
  /**
   * @deprecated
   * This prop is deprecated in favor of the 'endIcon' prop. Its usage is discouraged due to
   * issues managing the loading state in addition to the adornment in the {@link TextField}
   * component.
   */
  readonly endAdornment?: NonNullable<RootTextFieldProps<Variant>['InputProps']>['endAdornment'];
  /**
   * @deprecated
   * This prop is deprecated in favor of the 'startIcon' prop. Its usage is discouraged due to
   * issues managing the loading state in addition to the adornment in the {@link TextField}
   * component.
   */
  readonly startAdornment?: NonNullable<
    RootTextFieldProps<Variant>['InputProps']
  >['startAdornment'];
} & Omit<NonNullable<RootTextFieldProps<Variant>['InputProps']>, 'endAdornment' | 'startAdornment'>;

export type TextFieldProps<Variant extends types.TextFieldVariant = types.TextFieldVariant> = {
  /**
   * Defines whether or not the text field is disabled.
   *
   * @deprecated
   * This prop is deprecated in favor of the 'isDisabled' prop.
   *
   * @default false
   */
  /* eslint-disable-next-line react/boolean-prop-naming -- Deprecated MUI passthrough prop. */
  readonly disabled?: boolean;
  readonly endIcon?: IconProp;
  /**
   * Defines whether or not the {@link TextField} is in an error state, and optionally, what error
   * message should be displayed under the field in the case that the {@link TextField} is in an
   * error state.
   *
   * If provided as 'true', the field will be marked as being in an error state by changing the
   * field's border color, text color and helper text color to the theme's error color.  If
   * provided as a string, the same will apply, but the provided string will replace the helper
   * text under the field.
   */
  readonly error?: boolean | string;
  /**
   * Defines the level of the error state for the {@link TextField}.  If set to 'warning', the
   * field will be styled using warning colors instead of error colors.  If set to 'error', the
   * field will be styled based on the theme's error colors - which is the default behavior
   * in the case that the 'error' prop is defined and truthy.
   *
   * This prop does not determine whether or not the {@link TextField} is in an error state, or
   * whether or not the {@link TextField} is in a warning state - that is determined solely by
   * the 'error' prop.  This prop only controls the styling of the field when it is in an error
   * state.
   *
   * @default 'error'
   */
  readonly errorLevel?: 'error' | 'warning';
  readonly InputProps?: TextFieldInputProps;
  readonly isClearable?: boolean;
  readonly isClearVisible?: boolean;
  /**
   * Defines whether or not the {@link TextField} is disabled.
   *
   * @default false
   */
  readonly isDisabled?: boolean;
  /**
   * Defines whether or not the {@link TextField} is in a loading state.  When the {@link TextField}
   * is in a loading state, a loading indicator will appear within the field at the end position,
   * replacing the 'endIcon' temporarily if provided.
   */
  readonly isLoading?: boolean;
  /**
   * Defines whether or not the label is displayed as required and the input element is required.
   *
   * @default false
   */
  readonly isRequired?: boolean;
  readonly onClear?: (e: TextFieldClearEvent) => void;
  /**
   * Defines whether or not the label is displayed as required and the input element is required.
   *
   * @deprecated
   * This prop is deprecated in favor of the 'isRequired' prop.
   *
   * @default false
   */
  /* eslint-disable-next-line react/boolean-prop-naming -- Deprecated MUI passthrough prop. */
  readonly required?: boolean;
  readonly size?: `${InputSize}`;
  readonly startIcon?: IconProp;
} & Omit<RootTextFieldProps<Variant>, 'disabled' | 'error' | 'InputProps' | 'required' | 'size'>;

const TextFieldAdornment: FC<{
  readonly children: ReactNode;
  readonly position: 'end' | 'start';
}> = ({ children, position }) => (
  <InputAdornment
    position={position}
    sx={{
      '& > *': {
        maxHeight: '100%',
        minHeight: '100%',
        width: 'auto',
      },
      height: '100%',
      maxHeight: '100%',
      minHeight: '100%',
      width: 'auto',
    }}
  >
    {children}
  </InputAdornment>
);

export const TextField = <Variant extends TextFieldVariant = TextFieldVariant>({
  endIcon,
  error,
  errorLevel = 'error',
  isClearable = false,
  isClearVisible = true,
  isDisabled,
  isLoading,
  isRequired,
  onClear,
  ref,
  size = DefaultInputSize,
  startIcon,
  ...props
}: { readonly ref?: ForwardedRef<HTMLDivElement> } & TextFieldProps<Variant>) => {
  const hasError = (typeof error === 'string' && error.trim().length !== 0) || Boolean(error);

  const isCustomEndAdornmentVisible =
    endIcon !== undefined ||
    Boolean(isLoading) ||
    isClearable ||
    (isLoading !== undefined && props.InputProps?.endAdornment === undefined);

  return (
    <RootTextField
      {...props}
      data-attr-size={size}
      disabled={isDisabled ?? props.disabled}
      error={hasError}
      FormHelperTextProps={{
        component: HelperText,
        ...props.FormHelperTextProps,
        level: errorLevel,
      }}
      helperText={typeof error === 'string' && error.trim().length !== 0 ? error : props.helperText}
      InputProps={{
        ...props.InputProps,
        endAdornment: isCustomEndAdornmentVisible ? (
          <TextFieldAdornment position='end'>
            {(endIcon !== undefined || (isLoading && !isClearable)) && (
              <Icon icon={endIcon} isLoading={isLoading} />
            )}
            {props.InputProps?.endAdornment}
            {isClearable && (
              <TextInputClearButton
                className='text-field-clear-button'
                icon={CloseOutlined}
                isDisabled={props.disabled}
                isLoading={isLoading}
                isVisible={
                  (typeof props.value === 'string' && props.value.trim().length > 0) ||
                  isClearVisible
                }
                onClick={onClear}
              />
            )}
          </TextFieldAdornment>
        ) : (
          props.InputProps?.endAdornment
        ),
        startAdornment: startIcon ? (
          <TextFieldAdornment position='start'>
            <Icon icon={startIcon} isLoading={isLoading} />
          </TextFieldAdornment>
        ) : (
          props.InputProps?.startAdornment
        ),
      }}
      ref={ref}
      required={isRequired ?? props.required}
      sx={mergeSx(
        hasError && errorLevel === 'warning' ? TextFieldWarningLevelSx : undefined,
        props.sx,
        isLoading ? { pointerEvents: 'none' } : undefined,
      )}
    />
  );
};
